import { ChevronDown } from "lucide-react";
import React, { useState } from "react";
import { notificationAPI } from "../../../services/notificationAPI";
import { toast } from "react-toastify";

const rejectionReasonsList = [
  "Property documents not clear",
  "Not satisfied with legal documents",
  "Higher active accounts",
  "Settlement amount",
  "Suit-file, written-off",
  "Irregular payments",
  "Business not valid",
  "No proper Tax filings",
  "No GST",
  "No Transactions statement",
];

const ReviewDecision = ({
  handleLoanStatusChange,
  application,
  onSendToBank,
}) => {
  const [note, setNote] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedReasons, setSelectedReasons] = useState([]);

  const toggleReason = (reason) => {
    setSelectedReasons((prev) =>
      prev.includes(reason)
        ? prev.filter((r) => r !== reason)
        : [...prev, reason]
    );
  };

  const sendNotification = async (
    userId,
    note,
    notificationType,
    senderUserId
  ) => {
    try {
      const notification = await notificationAPI.sendNotification(
        userId,
        note,
        notificationType,
        senderUserId
      );
      toast.success(`Notification sent: ${note}`);
    } catch (error) {
      console.error("Error sending notification:", error);
      toast.error("Could not send notification.");
    }
  };

  const updateStatus = async (newStatus) => {
    await handleLoanStatusChange(newStatus, note);
  };

  const handleReject = async () => {
    const reasonText =
      selectedReasons.length > 0
        ? `Application rejected due to: ${selectedReasons.join(", ")}`
        : "Application Rejected";
    setNote(reasonText);
    await updateStatus("rejected");
    await sendNotification(
      application?.user?._id,
      reasonText,
      "Credit Manager",
      application?.assignedCreditManager?._id
    );
    setDropdownOpen(false);
    setSelectedReasons([]);
  };

  const handleSendToBank = async () => {
    setIsLoading(true);
    try {
      const bankNote = "New application is sent by Credit Manager";
      setNote(bankNote);
      if (onSendToBank) {
        onSendToBank({ note: bankNote });
      }
    } catch (error) {
      console.error("Error in handleSendToBank:", error);
    } finally {
      setIsLoading(false);
      setNote("");
    }
  };

  return (
    <div className="bg-[#131313] rounded-lg p-4">
      <div className="flex items-center mb-4">
        <span className="text-[#FFCA20] text-lg mr-2">✓</span>
        <h3 className="text-white text-lg">Review Decision</h3>
      </div>

      <textarea
        className="bg-[#131313] w-full text-white px-4 py-2 rounded-md mb-4 min-h-[100px] border border-[#333333] focus:outline-none focus:border-gray-500"
        placeholder="Enter Note..."
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />

      <button
        onClick={async () => {
          await updateStatus("in-review");
          await sendNotification(
            application?.assignedTo?._id,
            note,
            "Credit Manager",
            application?.assignedCreditManager?._id
          );
        }}
        className="bg-gray-800 w-full text-white px-4 py-2 rounded-md mb-4 flex items-center justify-center hover:bg-gray-700"
      >
        Send Back To Staff
      </button>

      {/* Rejection Options */}
      {dropdownOpen && (
        <div className="bg-[#1e1e1e] border border-red-600 rounded-md mb-4 p-2">
          <h4 className="text-white mb-2 font-medium">
            Select Rejection Reasons:
          </h4>
          {rejectionReasonsList.map((reason) => (
            <label
              key={reason}
              className="flex items-center px-2 py-1 text-white hover:bg-red-800 rounded cursor-pointer"
            >
              <input
                type="checkbox"
                className="mr-2"
                checked={selectedReasons.includes(reason)}
                onChange={() => toggleReason(reason)}
              />
              {reason}
            </label>
          ))}
        </div>
      )}

      {/* Reject Button */}
      <button
        onClick={() => {
          if (dropdownOpen) {
            handleReject();
          } else {
            setDropdownOpen(true);
          }
        }}
        className="bg-red-600 w-full text-white px-4 py-2 rounded-md flex items-center justify-center hover:bg-red-700"
      >
        {dropdownOpen ? "Confirm Rejection 🚫" : "Reject Application"}
      </button>

      <button
        onClick={handleSendToBank}
        disabled={isLoading}
        className="bg-green-500 w-full text-white px-4 py-2 rounded-md mt-4 flex items-center justify-center hover:bg-green-600 disabled:opacity-50"
      >
        {isLoading ? "Processing..." : "Send to Bank(s) for Approval →"}
      </button>
    </div>
  );
};

export default ReviewDecision;
